import React, { useState, useEffect } from "react";
import warningIcon from "../../../assets/images/warning-icon.png";
import { useTranslation } from "react-i18next";
import { ResponsiveBox, Icon, Typography } from "../../primitives";

function hasErrorValue(error) {
  return (
    error &&
    ((Array.isArray(error) && error.length > 0) ||
      (typeof error === "string" && error.trim().length > 0) ||
      (typeof error === "object" && !Array.isArray(error)))
  );
}

const ErrorMessage = (props) => {
  const { t } = useTranslation();
  const {
    error,
    errorRef,
    mt = 2,
    mb = 0,
    className = "manageUsers__errorMsg__container",
    sx = {},
    ...otherProps
  } = props;

  const hasError = hasErrorValue(error);
  const errorClass =
    Array.isArray(error) && error.length
      ? { marginTop: "-12px" }
      : { marginTop: "8px", marginLeft: "8px" };

  // Single full string for screen readers so NVDA announces "Error notification" + full message consistently
  const getFullAnnouncementText = () => {
    const prefix = "alert Error notification. ";
    if (Array.isArray(error) && error.length) {
      const title = t("erps.common.error.title");
      const items = error.map((err) => (typeof err === "object" && err?.errorMessage ? err.errorMessage : err)).join(". ");
      return prefix + title + " " + items;
    }
    if (typeof error === "string") return prefix + error;
    if (typeof error === "object" && error !== null) {
      let msg = t("erps.error.systemError");
      if (error.timestamp) msg += " [" + error.timestamp + "]";
      if (error.traceId) msg += " " + t("erps.common.label.traceID") + " " + error.traceId;
      if (error.errorMessage) msg += " " + error.errorMessage;
      return prefix + msg;
    }
    return prefix;
  };

  const [isShowDetails, setIsShowDetails] = useState(false);

  // NVDA announces live regions on *change*. Populate announcement after a tick so the span
  // is in the DOM empty first, then updates to the message (triggers announcement).
  const [announcementText, setAnnouncementText] = useState("");
  useEffect(() => {
    if (!hasError) {
      setAnnouncementText("");
      return;
    }
    setAnnouncementText("");
    const id = setTimeout(() => {
      setAnnouncementText(getFullAnnouncementText());
    }, 150);
    return () => clearTimeout(id);
  }, [error, hasError]);

  function renderMoreDetailsButton() {
    return (
      <button 
        onClick={() => setIsShowDetails(prev => !prev)} 
        aria-expanded={isShowDetails}
        style={{
          fontSize: '0.8rem',
          cursor: 'pointer',
          color: '#A11324',
          marginLeft: '1rem'
        }}
      >
        {isShowDetails ? "− " + t("erps.common.label.lessDetails") : "+ " + t("erps.common.label.moreDetails")}
      </button>
    );
  }

  return (
    <ResponsiveBox
      className={className}
      mt={hasError ? mt : 0}
      mb={hasError ? mb : 0}
      sx={{
        display: hasError ? "block" : "none",
        ...sx,
      }}
      {...otherProps}
    >
      {hasError && (
        <ResponsiveBox ref={errorRef} style={{ display: "flex" }}>
          {/* Live region: content set after delay so NVDA sees a change and announces */}
          <span
            className="visually-hidden"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            {announcementText}
          </span>
          <div aria-hidden="true" style={{ display: "flex", flex: 1 }}>
          <Icon className="manageUsers__error__icon">
            <img src={warningIcon} alt="" />
          </Icon>
          <div style={{ ...errorClass }}>
            {Array.isArray(error) ? (
              error.length ? (
                <ul>
                  {" "}
                  <li
                    style={{
                      color: "#C12334",
                      listStyle: "none",
                      marginLeft: "-20px",
                      marginTop: "20px",
                    }}
                  >
                    {t("erps.common.error.title") /* This should be used only for validation messages:  "The following error(s) need to be fixed:" */ }
                  </li>
                  {error.map((err, i) => (
                    <li style={{ color: "#C12334" }} key={i}> {err.errorMessage || err} </li>
                  ))}
                </ul>
              ) : null
            ) : (typeof error === "string" ? (
                <Typography variant="inherit" style={{ color: "#C12334", whiteSpace: "pre-wrap" }}>
                  {error}
                </Typography>
              ) : (
                <>
                  <Typography variant="inherit" style={{ color: "#C12334" }}>
                    {t("erps.error.systemError") + ". "}
                    {error.timestamp && (" [" + error.timestamp + "]")}
                    {error.traceId && (" " + t("erps.common.label.traceID") + " " + error.traceId)}
                    {error.errorMessage && (<><br/>{error.errorMessage}</>)}
                    {(error.showDetails == true && (error.error || error.rootCause)) && renderMoreDetailsButton()}
                  </Typography>
                  {isShowDetails && (
                    <div style={{ fontSize: '0.8rem', margin: '0px', color: '#C12334',  border: '1px solid #DDDDDD', padding: '4px' }}>
                      {error.error || ""}
                      {error.error && error.rootCause ? (<br/>) : ("")}
                      {error.rootCause && ("Root Cause: " + error.rootCause)}
                    </div>
                  )}
                </>
              ))
            }
          </div>
          </div>
        </ResponsiveBox>
      )}
    </ResponsiveBox>
  );
};

export default ErrorMessage;
